import clsx from 'clsx';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { CHROME_STORE_URL } from '../config';

export const questions = [
  {
    question: 'Does it work automatically?',
    response: (
      <p>
        Yes! Once you install the extension, it will automatically detect when you join a Google Meet meeting and start
        tracking attendance without any additional setup required from your side.
      </p>
    )
  },
  {
    question: 'Where is my attendance data stored?',
    response: (
      <p>
        Depending on the plan you choose, your attendance data is either stored locally on your device (free plan) or
        securely in our cloud servers (paid plans). We prioritize data security and privacy, ensuring that your
        information is protected at all times.
      </p>
    )
  },
  {
    question: 'Do you share or sell my data to third parties?',
    response: (
      <p>
        No, we do not share or sell your data to third parties. Your privacy is our top priority, and we are committed
        to protecting your information.
      </p>
    )
  },
  {
    question: 'How many participants can the extension track?',
    response: (
      <p>
        The extension is not limiting the number of participants it can track. However, your Google Meet session may
        have its own limitations based on your Google account plan. For example, free Google accounts are limited to 100
        participants per meeting.
      </p>
    )
  },
  {
    question: 'Can I export attendance reports to Excel/CSV?',
    response: (
      <p>
        Yes, we offer the ability to export attendance reports in both Excel and CSV formats, making it easy for you to
        analyze and share the data as needed.
      </p>
    )
  },
  {
    question: 'Does it track late arrivals and early departures?',
    response: (
      <p>
        Yes, the extension tracks (in background) the exact join and leave times of each participant, allowing you to
        monitor late arrivals effectively.
      </p>
    )
  },
  {
    question: 'What about browser support?',
    response: (
      <p>
        Meet List is available for Chrome or any other Chromium based browsers. You can get it from the {' '}
        <a
          className="flat"
          href={CHROME_STORE_URL}
          target="_blank"
          rel="noreferrer"
        >
          Google Chrome Store
        </a>.
      </p>
    ),
  },
  {
    question: 'What are your supported languages?',
    response: (
      <p>
        The Premium dashboard we currently have English and we're working on other languages as we speak. Our browser
        extension <u>Google Meet Attendance List</u> has support for English, and Spanish languages.
      </p>
    ),
  },
  {
    question: 'Does Meet List work on mobile?',
    response: 'No. It was designed to work as a browser extension.',
  },
  {
    question: 'How can I get in contact?',
    response: (
      <p>
        You can always use the <b>Feedback</b> form located on the sidebar or just send an email
        to <u>contact@meetlist.io</u>
      </p>
    )
  },
  {
    question: 'Can I track attendance for recurring meetings?',
    response: (
      <p>
        Yes, Meet List can track attendance for recurring meetings. Each session will be recorded separately, allowing
        you to monitor attendance over time.
      </p>
    )
  },
  {
    question: 'Can meeting participants see that I\'m tracking attendance?',
    response: (
      <p>
        No, the attendance tracking is done discreetly through the extension, and participants will not be notified that
        their attendance is being tracked.
      </p>
    )
  },
  {
    question: 'What\'s the difference between free and premium versions?',
    response: (
      <p>
        The free version offers basic attendance tracking features, while the premium version includes advanced
        functionalities such as detailed reports, cloud storage, multiple export options and priority support. Check
        out our pricing page for a full comparison.
      </p>
    )
  },
  {
    question: 'Why is the VAT not included in the price?',
    response: (
      <p>
        VAT is not included because it varies from country to country. For example – Germany has a 19% VAT, while
        France has a 20% VAT.
      </p>
    ),
  },
  {
    question: 'I accidentally made a purchase. Can I get a refund?',
    response: (
      <p>
        Sure, we all make mistakes sometimes, right? Message <b>contact@meetlist.io</b> and we’ll review the case which
        will result in a complete refund most of the time.
      </p>
    ),
  },
  {
    question: 'Can I cancel my subscription at any time?',
    response: (
      <p>
        Yes, you can cancel your subscription at any time through your account settings. Your access to premium features
        will continue until the end of your current billing cycle.
      </p>
    ),
  }
];

export default function FAQ({ className, limit }) {
  const displayedQuestions = limit ? questions.slice(0, limit) : questions;

  return (
    <section
      id="faq"
      className={clsx(
        'container mx-auto px-4 py-16',
        className,
      )}
    >
      <h2 className="text-center text-2xl mb-5">
        Frequently asked questions
      </h2>
      <div className="mx-auto w-full max-w-xl rounded-2xl bg-white p-2">
        {
          displayedQuestions.map((item, index) => (
            <Disclosure  as="div" className="mt-2" key={'faq-' + index}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full justify-between rounded-lg bg-gray-100 px-4 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75">
                    <span>{ item.question }</span>
                    <ChevronUpIcon
                      className={`${
                        open ? 'rotate-180 transform' : ''
                      } h-5 w-5 text-gray-500`}
                    />
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-500">
                    { item.response }
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))
        }

        {
          limit && (
            <div className="flex justify-center mt-4">
              <a href="/faq" className="secondary">
                Show More
              </a>
            </div>
          )
        }
      </div>
    </section>
  )
}
